/**
 * Admin API client — Financial Institutions + Loan Products CRUD
 * Used by DataEntryPortal (data_operator role) and AdminPortal (admin role)
 */

import { authenticatedFetch } from './authApi'

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface FinancialInstitution {
  id: string
  name: string
  code: string
  logo_url: string | null
  is_active: boolean
  created_at: string | null
}

export interface FinancialInstitutionCreate {
  name: string
  code: string
  logo_url?: string
}

export interface LoanProduct {
  id: string
  institution_id: string
  institution_name: string
  institution_code: string
  product_name: string
  product_type: string
  interest_rate: number       // display % e.g. 6.14 (backend multiplies by 100 on GET)
  comparison_rate: number     // display % e.g. 6.21
  max_lvr: number
  fees: {
    application_fee?: number
    annual_fee?: number
    discharge_fee?: number
  }
  features: {
    offset_account?: boolean
    redraw_facility?: boolean
    extra_repayments?: boolean
    portability?: boolean
  }
  policy_tags: Record<string, string[]>
  loan_purpose: string | null
  is_active: boolean
  source: string
  source_url: string | null
  last_scraped_at: string | null
  data_quality_score: number | null
  created_at: string | null
}

export interface LoanProductCreate {
  institution_id: string
  product_name: string
  product_type: string
  interest_rate: number       // send raw % e.g. 6.14, backend divides by 100
  comparison_rate: number
  max_lvr: number
  fees: {
    application_fee?: number
    annual_fee?: number
    discharge_fee?: number
  }
  features: {
    offset_account?: boolean
    redraw_facility?: boolean
    extra_repayments?: boolean
    portability?: boolean
  }
  policy_tags: Record<string, string[]>
  loan_purpose?: string | null
  is_active: boolean
  source_note?: string
}

export interface ProductFilters {
  institution_id?: string
  product_type?: string
  is_active?: boolean
}

// ── Financial Institutions ────────────────────────────────────────────────────

export async function getFinancialInstitutions(token: string): Promise<FinancialInstitution[]> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/financial-institutions`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ items: FinancialInstitution[] }>(res)
  return data.items
}

export async function createFinancialInstitution(
  token: string,
  body: FinancialInstitutionCreate,
): Promise<FinancialInstitution> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/financial-institutions`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<FinancialInstitution>(res)
}

export async function updateFinancialInstitution(
  token: string,
  id: string,
  body: FinancialInstitutionCreate,
): Promise<FinancialInstitution> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/financial-institutions/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<FinancialInstitution>(res)
}

export async function toggleFinancialInstitutionStatus(
  token: string,
  id: string,
): Promise<FinancialInstitution> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/financial-institutions/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token),
  })
  return handleResponse<FinancialInstitution>(res)
}

export async function deleteFinancialInstitution(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/financial-institutions/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}

// ── Loan Products ─────────────────────────────────────────────────────────────

export async function getLoanProducts(
  token: string,
  filters?: ProductFilters,
): Promise<LoanProduct[]> {
  const params = new URLSearchParams()
  if (filters?.institution_id) params.set('institution_id', filters.institution_id)
  if (filters?.product_type) params.set('product_type', filters.product_type)
  if (filters?.is_active !== undefined) params.set('is_active', String(filters.is_active))
  const qs = params.toString() ? `?${params.toString()}` : ''
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products${qs}`, {
    headers: authHeaders(token),
  })
  const data = await handleResponse<{ items: LoanProduct[]; count: number }>(res)
  return data.items
}

export async function getLoanProduct(token: string, id: string): Promise<LoanProduct> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products/${id}`, {
    headers: authHeaders(token),
  })
  return handleResponse<LoanProduct>(res)
}

export async function createLoanProduct(
  token: string,
  body: LoanProductCreate,
): Promise<LoanProduct> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<LoanProduct>(res)
}

export async function updateLoanProduct(
  token: string,
  id: string,
  body: LoanProductCreate,
): Promise<LoanProduct> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products/${id}`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<LoanProduct>(res)
}

export async function toggleLoanProductStatus(token: string, id: string): Promise<LoanProduct> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products/${id}/status`, {
    method: 'PATCH',
    headers: authHeaders(token),
  })
  return handleResponse<LoanProduct>(res)
}

export async function deleteLoanProduct(token: string, id: string): Promise<void> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/admin/loan-products/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  })
  await handleResponse<void>(res)
}
